import { snackbar } from "@mobiscroll/react5";
import {
  getQueryWithParams,
  postMutation,
} from "src/components/ApolloConnection/ApolloProviderComponent";
import {
  REFER_TO,
  START_CONSULTATION,
} from "src/components/graphql/GraphQLmutation";
import {
  CHECK_CONSULTATION,
  GET_APPOINTMENT_DETAILS,
  GET_PATIENT,
  LIST_DOCTORS,
  LIST_PATIENT_HISTORY,
  LIST_PATIENT_SYMPTOMS,
} from "src/components/graphql/GraphQLquery";

export async function getPatientBasicDetails(data) {
  try {
    const response = await getQueryWithParams(GET_PATIENT, { id: data });
    
    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch patient information");
    }

    const result = response?.data?.getPatient;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch patient information");
    }
    
    // Success case
    if (result?.data) {
      snackbar({
        message: result?.message || "Patient information fetched successfully.",
        color: "success",
      });
      return result.data;
    }
    throw new Error("No patient data returned");
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at getPatientBasicDetails:", e);
    return null;
  }
}

export async function listPatientHistory(patientId, start, end, filters) {
  try {
    const response = await getQueryWithParams(LIST_PATIENT_HISTORY(patientId, String(start), String(end), filters));

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch patient history");
    }

    const result = response?.data?.listPatientHistory;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch patient history");
    }
    
    // Success case
    const historyData = result?.data;

    if (historyData && Array.isArray(historyData) && historyData.length > 0) {
      snackbar({ 
        message: result?.message || "Patient medical history fetched successfully.", 
        color: "success" 
      });
      return historyData;
    }
    return historyData || [];
  } catch (e) {
    snackbar({ 
      message: e.message, 
      color: "danger" 
    });
    console.error("Error at listPatientHistory:", e);
    return null;
  }
}

export async function referTo(data) {
  try {
    const response = await postMutation(REFER_TO, data);

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to refer patient");
    }

    const result = response?.data?.referPatientTo;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to refer patient");
    }
    
    // Success case
    snackbar({
      message: result?.message || "Patient referred successfully.",
      color: "success",
    });
    return true;
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at referTo:", e);
    return false;
  }
}

export async function startConsultation(data) {
  try {
    const response = await postMutation(START_CONSULTATION, data);

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to start consultation");
    }

    const result = response?.data?.startConsultation;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to start consultation");
    }
    
    // Success case
    return true;
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at startConsultation:", e);
    return false;
  }
}

export async function getConsultationDetails(data) {
  try {
    const response = await getQueryWithParams(CHECK_CONSULTATION, data);

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch consultation details");
    }

    const result = response?.data?.getAppointment;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch consultation details");
    }
    
    // Success case
    if (result?.data) {
      return result.data;
    }
    throw new Error("No consultation data returned");
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at getConsultationDetails:", e);
    return null;
  }
}

export async function getAppointmentDetails(data) {
  try {
    const response = await getQueryWithParams(GET_APPOINTMENT_DETAILS, data);
    
    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch appointment details");
    }

    const result = response?.data?.getAppointment;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch appointment details");
    }
    
    // Success case
    if (result?.data) {
      return result.data;
    }
    throw new Error("No appointment data returned");
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at getAppointmentDetails:", e);
    return null;
  }
}

export async function listSymptoms(patientId) {
  try {
    const response = await getQueryWithParams(LIST_PATIENT_SYMPTOMS(patientId));

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch symptoms");
    }

    const result = response?.data?.getAppointment;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch symptoms");
    }
    
    // Success case
    return result?.data || null;
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at listSymptoms:", e);
    return null;
  }
}

export async function listDoctors() {
  try {
    const response = await getQueryWithParams(LIST_DOCTORS);

    if (!response || response.errors) {
      throw new Error(response?.errors?.[0]?.message || "Failed to fetch doctors list");
    }

    const result = response?.data?.listDoctors;
    
    // Check for error response (statusCode >= 400)
    if (result?.statusCode && result.statusCode >= 400) {
      throw new Error(result?.message || "Failed to fetch doctors list");
    }
    
    // Success case
    return result?.data || [];
  } catch (e) {
    snackbar({
      message: e.message,
      color: "danger",
    });
    console.error("Error at listDoctors:", e);
    return [];
  }
}
